import { Component } from '@theme/component';
import { isPickupPromptRequired } from '@theme/store-selection-state';

let STORAGE_KEY = 'amb_store_selection';
let SELECTION_CHANGED_EVENT = 'store-selection:changed';
let SELECTION_CLEARED_EVENT = 'store-selection:cleared';

class CartPickupStoreSelection extends Component {
  #boundOnChanged;
  #boundOnCleared;
  #boundOnStorage;
  #stateObserver;

  connectedCallback() {
    super.connectedCallback();
    renderFromStorage(this);
    this.#boundOnChanged = this.#onSelectionChanged.bind(this);
    this.#boundOnCleared = this.#onSelectionCleared.bind(this);
    this.#boundOnStorage = this.#onStorageEvent.bind(this);
    // Match `emitEvent()` in assets/store-selection-state.js (fires on window; does not bubble to document).
    window.addEventListener(SELECTION_CHANGED_EVENT, this.#boundOnChanged);
    window.addEventListener(SELECTION_CLEARED_EVENT, this.#boundOnCleared);
    window.addEventListener('storage', this.#boundOnStorage);

    this.#stateObserver = new MutationObserver(() => {
      if (this.dataset.state === 'empty' && readStoreSelection()) {
        renderFromStorage(this);
      }
    });
    this.#stateObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-state'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(SELECTION_CHANGED_EVENT, this.#boundOnChanged);
    window.removeEventListener(SELECTION_CLEARED_EVENT, this.#boundOnCleared);
    window.removeEventListener('storage', this.#boundOnStorage);
    this.#stateObserver?.disconnect();
  }

  /**
   * Re-read `amb_store_selection` from localStorage and update UI.
   * Used when the drawer opens after selection was committed, and by `cart-drawer-component`.
   */
  syncFromStorage() {
    renderFromStorage(this);
  }

  #onSelectionChanged() {
    // Selection is persisted before `store-selection:changed`; always read canonical JSON from storage.
    // Event `detail` is `{ source, selection }`, not suitable to pass straight into renderPayload().
    renderFromStorage(this);
  }

  #onSelectionCleared() {
    renderEmpty(this);
  }

  // Cross-tab sync via the native `storage` event.
  #onStorageEvent(event) {
    if (event.key !== STORAGE_KEY) return;
    renderFromStorage(this);
  }
}

let renderFromStorage = (element) => {
  let payload = readStoreSelection();
  if (!payload) {
    renderEmpty(element);
    return;
  }
  renderPayload(element, payload);
};

let renderEmpty = (element) => {
  element.dataset.state = 'empty';
  setPickupPrompt(element, false);
  setText(element, 'storeName', '');
  setText(element, 'storeAddress', '');
  setText(element, 'pickupDay', '');
  setText(element, 'pickupSlot', '');
};

let renderPayload = (element, payload) => {
  element.dataset.state = 'ready';
  setText(element, 'storeName', payload.name ?? '');
  setText(
    element,
    'storeAddress',
    formatAddress(payload.address ?? payload.details?.address)
  );

  // A bakery can be saved with no pickup at all (MF10 hydration) or with a day and
  // slot that have since elapsed. Both left the "When" rows as bare icons, so show
  // the prompt in their place — the cart is the last surface before checkout.
  let promptRequired = isPickupPromptRequired(payload);
  setPickupPrompt(element, promptRequired);
  setText(element, 'pickupDay', promptRequired ? '' : formatPickupDay(payload));
  setText(element, 'pickupSlot', promptRequired ? '' : formatPickupSlot(payload.pickupSlot));
};

let setPickupPrompt = (element, required) => {
  element.dataset.pickupIncomplete = required ? 'true' : 'false';
  let prompt = element.refs.pickupPrompt;
  if (prompt) prompt.hidden = !required;
};

let setText = (element, refName, value) => {
  let node = element.refs[refName];
  if (!node) return;
  node.textContent = value;
};

let readStoreSelection = () => {
  try {
    let raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.warn('cart-pickup-store-selection: invalid localStorage payload', error);
    return null;
  }
};

let formatAddress = (address) => {
  if (!address) return '';
  let line1 = [address.address1, address.address2].filter(Boolean).join(' ');
  let line2 = [address.city, address.province ?? address.provinceCode, address.zip]
    .filter(Boolean)
    .join(' ');
  return [line1, line2, address.country].filter(Boolean).join(', ');
};

let DAY_LABEL_OVERRIDES = {
  today: 'Today',
  tomorrow: 'Tomorrow',
};

let formatPickupDay = (payload) => {
  let dayKey = (payload.pickupDay ?? '').toString().toLowerCase();
  let label = DAY_LABEL_OVERRIDES[dayKey] ?? capitalize(payload.pickupDay ?? '');
  let date = payload.pickupDate?.toString().trim();
  if (label && date) return `${label}, ${date}`;
  return label || date || '';
};

let SLOT_RANGE_PATTERN = /^(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})$/;

// Convert a 24h pickup slot range like "10:00-11:00" into "10:00am - 11:00am".
// Falls back to the raw value when the format does not match.
let formatPickupSlot = (slot) => {
  if (!slot) return '';
  let match = SLOT_RANGE_PATTERN.exec(slot.trim());
  if (!match) return slot;
  let [, sH, sM, eH, eM] = match;
  return `${to12h(Number(sH), Number(sM))} - ${to12h(Number(eH), Number(eM))}`;
};

let to12h = (hours, minutes) => {
  let meridiem = hours >= 12 ? 'pm' : 'am';
  let displayHour = hours % 12 === 0 ? 12 : hours % 12;
  let minutePart = minutes === 0 ? '00' : String(minutes).padStart(2, '0');
  return `${displayHour}:${minutePart}${meridiem}`;
};

let capitalize = (value) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

if (!customElements.get('cart-pickup-store-selection')) {
  customElements.define('cart-pickup-store-selection', CartPickupStoreSelection);
}
